import { useState } from "react";
import { useListTestimonials } from "@workspace/api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Plus, Edit2, Trash2, Star } from "lucide-react";
import { format } from "date-fns";
import { TestimonialDialog } from "@/components/admin/TestimonialDialog";

export default function Testimonials() {
  const queryClient = useQueryClient();
  const { data: testimonialsData, refetch } = useListTestimonials({ all: "true" } as any);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState<any>(null);
  
  const deleteTestimonial = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const res = await fetch(`/api/testimonials/${id}/delete`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete testimonial");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/testimonials"] });
    }
  });

  const testimonials = testimonialsData || [];

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this testimonial?")) {
      try {
        await deleteTestimonial.mutateAsync({ id });
      } catch (error) {
        console.error("Failed to delete testimonial:", error);
      }
    }
  };

  const handleAdd = () => {
    setSelectedTestimonial(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (testimonial: any) => {
    setSelectedTestimonial(testimonial);
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold mb-2">Testimonials</h1>
          <p className="text-muted-foreground">Manage client reviews and feedback.</p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="mr-2 h-4 w-4" /> Add Testimonial
        </Button>
      </div>

      <TestimonialDialog 
        open={isDialogOpen} 
        onOpenChange={setIsDialogOpen} 
        testimonial={selectedTestimonial}
      />

      <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Company & Role</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {testimonials.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                  No testimonials found. Click "Add Testimonial" to create one.
                </TableCell>
              </TableRow>
            ) : (
              testimonials?.map((test: any) => (
                <TableRow key={test.id}>
                  <TableCell className="font-medium">{test.name}</TableCell>
                  <TableCell>
                    {test.role} @ {test.company}
                  </TableCell>
                  <TableCell>
                    <div className="flex text-yellow-500">
                      {Array.from({ length: test.rating || 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {test.published ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-500">Published</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">Draft</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(test)}><Edit2 className="h-4 w-4" /></Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-destructive"
                        onClick={() => handleDelete(test.id)}
                        disabled={deleteTestimonial.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
